import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Globe2, Mail, MapPin, Phone } from 'lucide-react';
import icon from '../assets/logo/veda.png';
import canIcon from '../assets/img/canada.png';
import usaIcon from '../assets/img/usa.png';

const noidaMapUrl = 'https://www.google.com/maps/place/Vedalytics+software+solutions/@28.6332208,77.2265091,11.6z/data=!4m10!1m2!2m1!1svedalytics+software+solutions+private+limited!3m6!1s0x390ce54929df6095:0xca68387e30f094ba!8m2!3d28.585129!4d77.313256!15sCi12ZWRhbHl0aWNzIHNvZnR3YXJlIHNvbHV0aW9ucyBwcml2YXRlIGxpbWl0ZWSSARBzb2Z0d2FyZV9jb21wYW554AEA!16s%2Fg%2F11wh5jtqt6?entry=ttu&g_ep=EgoyMDI1MDMyMy4wIKXMDSoASAFQAw%3D%3D';

const companyLinks = [
  { label: 'About Us', to: '/about' },
  { label: 'Contact Us', to: '/contact' },
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Terms & Conditions', to: '/terms' },
];

const indiaOffices = [
  { label: 'Delhi' },
  { label: 'Noida', href: noidaMapUrl },
  { label: 'Hyderabad' },
  { label: 'Bangalore' },
];

const footprints = [
  { label: 'Canada (Ontario)', icon: canIcon },
  { label: 'USA (Las Vegas)', icon: usaIcon },
];

export default function BottomContent() {
  return (
    <footer className="veda-footer">
      <div className="veda-footer-shell">
        <div className="veda-footer-brand">
          <img className="veda-footer-logo" src={icon} alt="Vedalytics logo" />
          <h2>Vedalytics Software Solutions Pvt. Ltd</h2>
          <p className="veda-footer-address">
            <MapPin size={18} />
            <span>811, 8th Floor, Vijaya Building, 17, Barakhamba Road, Connaught Place, New Delhi - 110001.</span>
          </p>
          <div className="veda-footer-contact-grid">
            <a href="https://www.vedalytics.com" target="_blank" rel="noopener noreferrer">
              <Globe2 size={17} />
              <span>www.vedalytics.com</span>
            </a>
            <a href="mailto:info@vedalytics.com">
              <Mail size={17} />
              <span>info@vedalytics.com</span>
            </a>
            <a href="tel:+911123739579">
              <Phone size={17} />
              <span className="india-flag veda-footer-contact-flag" aria-label="India flag" role="img">
                <span />
              </span>
              <span>+91-11-23739579</span>
            </a>
            <a href="tel:+17252959168">
              <Phone size={17} />
              <img className="veda-footer-contact-flag" src={usaIcon} alt="USA flag" />
              <span>+1 (725) 295-9168</span>
            </a>
          </div>
        </div>

        <div className="veda-footer-panel">
          <h3>Company</h3>
          <nav className="veda-footer-links" aria-label="Footer company links">
            {companyLinks.map((link) => (
              <Link key={link.to} to={link.to}>
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="veda-footer-panel">
          <h3 className="veda-footer-title-with-flag">
            India Offices
            <span className="india-flag" aria-label="India flag" role="img">
              <span />
            </span>
          </h3>
          <div className="veda-office-list">
            {indiaOffices.map((office) => {
              const content = (
                <>
                  <MapPin size={17} />
                  <span>{office.label}</span>
                  {office.href && <ExternalLink size={14} />}
                </>
              );

              return office.href ? (
                <a key={office.label} href={office.href} target="_blank" rel="noopener noreferrer">
                  {content}
                </a>
              ) : (
                <span key={office.label}>
                  {content}
                </span>
              );
            })}
          </div>
        </div>

        <div className="veda-footer-panel">
          <h3>Worldwide Footprints</h3>
          <div className="veda-footprint-list">
            {footprints.map((item) => (
              <div key={item.label} className="veda-footprint-item">
                <img src={item.icon} alt="" />
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className="veda-social-wrap">
            <h3>Follow Us</h3>
            <div className="veda-social-links">
              <a href="https://www.facebook.com/Vedalytics-Software-Solutions-Pvt-Ltd-100287189409351" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <i className="fab fa-facebook" />
              </a>
              <a href="https://twitter.com/vedalytics" target="_blank" rel="noopener noreferrer" aria-label="X">
                <i className="fab fa-x-twitter" />
              </a>
              <a href="https://www.linkedin.com/company/vedalytics-solutions/mycompany/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fab fa-linkedin" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="veda-footer-copy">
        © Copyright 2026. Vedalytics. All rights reserved.
      </div>

      <style>{`
        .veda-footer {
          position: relative;
          overflow: hidden;
          color: var(--heading_color);
          background:
            radial-gradient(circle at 12% 18%, rgba(15, 112, 240, 0.18), transparent 30%),
            radial-gradient(circle at 88% 8%, rgba(22, 163, 74, 0.16), transparent 26%),
            linear-gradient(135deg, var(--bottom_color), var(--bottom_color));
        }

        .veda-footer::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(15, 112, 240, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(15, 112, 240, 0.08) 1px, transparent 1px);
          background-size: 34px 34px;
          mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.65), transparent);
          pointer-events: none;
        }

        .veda-footer-shell {
          position: relative;
          z-index: 1;
          width: min(1180px, calc(100% - 32px));
          margin: 0 auto;
          padding: 54px 0 42px;
          display: grid;
          grid-template-columns: minmax(280px, 1.45fr) repeat(3, minmax(180px, 1fr));
          gap: 18px;
        }

        .veda-footer-brand,
        .veda-footer-panel {
          border: 1px solid rgba(15, 112, 240, 0.14);
          border-radius: 18px;
          background: color-mix(in srgb, var(--card_color) 86%, transparent);
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.12);
          backdrop-filter: blur(14px);
        }

        .veda-footer-brand {
          padding: 24px;
        }

        .veda-footer-panel {
          padding: 24px 22px;
        }

        .veda-footer-logo {
          width: 164px;
          height: auto;
          display: block;
          margin-bottom: 18px;
        }

        .veda-footer h2,
        .veda-footer h3 {
          margin: 0;
          color: var(--heading_color);
          letter-spacing: 0;
        }

        .veda-footer h2 {
          font-size: 1.18rem;
          font-weight: 800;
          line-height: 1.35;
        }

        .veda-footer h3 {
          font-size: 1.02rem;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .veda-footer-address,
        .veda-footer-contact-grid a,
        .veda-footer-links a,
        .veda-office-list a,
        .veda-office-list span,
        .veda-footprint-item {
          color: var(--content_color);
          font-size: 0.92rem;
          line-height: 1.5;
        }

        .veda-footer-address {
          display: grid;
          grid-template-columns: 22px 1fr;
          gap: 9px;
          margin: 14px 0 18px;
        }

        .veda-footer-address svg,
        .veda-footer-contact-grid svg,
        .veda-office-list svg {
          color: #0f70f0;
          flex: 0 0 auto;
        }

        .veda-footer-contact-grid,
        .veda-footer-links,
        .veda-office-list,
        .veda-footprint-list {
          display: grid;
          gap: 10px;
        }

        .veda-footer-contact-grid a,
        .veda-footer-links a,
        .veda-office-list a,
        .veda-office-list span {
          min-height: 38px;
          display: flex;
          align-items: center;
          gap: 9px;
          padding: 8px 10px;
          border-radius: 10px;
          color: var(--content_color);
          text-decoration: none;
          background: rgba(15, 112, 240, 0.07);
          transition: transform 0.2s ease, background-color 0.2s ease, color 0.2s ease;
        }

        .veda-footer-contact-grid a:hover,
        .veda-footer-links a:hover,
        .veda-office-list a:hover {
          color: #0f70f0;
          background: rgba(15, 112, 240, 0.14);
          transform: translateX(3px);
        }

        .veda-footer-title-with-flag {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .india-flag {
          width: 34px;
          height: 24px;
          display: inline-grid;
          place-items: center;
          overflow: hidden;
          border-radius: 5px;
          border: 1px solid rgba(15, 23, 42, 0.12);
          background: linear-gradient(to bottom, #ff9933 0 33.33%, #ffffff 33.33% 66.66%, #138808 66.66% 100%);
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
        }

        .india-flag span {
          width: 8px;
          height: 8px;
          border: 1.5px solid #000080;
          border-radius: 50%;
          background: transparent;
        }

        .veda-footer-contact-flag {
          width: 26px;
          height: 18px;
          flex: 0 0 auto;
          border-radius: 4px;
          object-fit: cover;
        }

        .india-flag.veda-footer-contact-flag {
          width: 26px;
          height: 18px;
          box-shadow: none;
        }

        .india-flag.veda-footer-contact-flag span {
          width: 6px;
          height: 6px;
          border-width: 1px;
        }

        .veda-footprint-item {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 42px;
          padding: 8px 10px;
          border-radius: 12px;
          background: rgba(22, 163, 74, 0.08);
        }

        .veda-footprint-item img {
          width: 36px;
          height: 36px;
          object-fit: contain;
        }

        .veda-social-wrap {
          margin-top: 22px;
          padding-top: 18px;
          border-top: 1px solid rgba(15, 112, 240, 0.15);
        }

        .veda-social-links {
          display: flex;
          gap: 10px;
        }

        .veda-social-links a {
          width: 40px;
          height: 40px;
          display: grid;
          place-items: center;
          border-radius: 50%;
          color: #ffffff;
          background: #0f70f0;
          text-decoration: none;
          box-shadow: 0 10px 22px rgba(15, 112, 240, 0.25);
          transition: transform 0.2s ease, background-color 0.2s ease;
        }

        .veda-social-links a:nth-child(2) {
          background: #111827;
        }

        .veda-social-links a:nth-child(3) {
          background: #0a66c2;
        }

        .veda-social-links a:hover {
          transform: translateY(-3px);
        }

        .veda-social-links i {
          font-size: 20px;
        }

        .veda-footer-copy {
          position: relative;
          z-index: 1;
          width: 100%;
          padding: 18px;
          text-align: center;
          color: #ffffff;
          background: #111827;
          font-size: 0.92rem;
        }

        [data-theme="dark"] .veda-footer-brand,
        [data-theme="dark"] .veda-footer-panel {
          border-color: rgba(255, 255, 255, 0.12);
          background: rgba(30, 34, 34, 0.78);
          box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);
        }

        [data-theme="dark"] .veda-footer-contact-grid a,
        [data-theme="dark"] .veda-footer-links a,
        [data-theme="dark"] .veda-office-list a,
        [data-theme="dark"] .veda-office-list span {
          background: rgba(255, 255, 255, 0.07);
        }

        [data-theme="dark"] .veda-footprint-item {
          background: rgba(255, 255, 255, 0.07);
        }

        @media (max-width: 991px) {
          .veda-footer-shell {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .veda-footer-brand {
            grid-column: 1 / -1;
          }
        }

        @media (max-width: 575px) {
          .veda-footer-shell {
            width: min(100% - 24px, 420px);
            grid-template-columns: 1fr;
            padding: 36px 0 30px;
          }

          .veda-footer-brand,
          .veda-footer-panel {
            padding: 20px;
            border-radius: 16px;
          }

          .veda-footer-logo {
            width: 148px;
          }
        }
      `}</style>
    </footer>
  );
}
